type StepStatus = 'todo' | 'active' | 'done'

function statusFrom(started: boolean, done: boolean): StepStatus {
  if (done) return 'done'
  if (started) return 'active'
  return 'todo'
}

const STEP_DESCRIPTIONS = [
  'Agents search the web for relevant information',
  'Each agent makes an independent prediction',
  'Supervisor synthesises all predictions',
  'Final probability and reasoning are calculated',
]

export default function ProgressSteps({
  predictionStarted,
  predictionDone,
  supervisorStarted,
  supervisorDone,
  finalDone,
}: {
  predictionStarted: boolean
  predictionDone: boolean
  supervisorStarted: boolean
  supervisorDone: boolean
  finalDone: boolean
}) {
  // Per your preference, "Agentic search" is folded into the "Agent predictions" stage.
  // We still show the two labels for clarity, but they progress together.
  const agenticSearch = statusFrom(predictionStarted, predictionDone)
  const agentPreds = statusFrom(predictionStarted, predictionDone)
  const supervisor = statusFrom(supervisorStarted, supervisorDone || finalDone)
  const final = statusFrom(finalDone, finalDone)

  const steps: Array<{ label: string; status: StepStatus; description: string }> = [
    { label: 'Agentic search', status: agenticSearch, description: STEP_DESCRIPTIONS[0] },
    { label: 'Agent predictions', status: agentPreds, description: STEP_DESCRIPTIONS[1] },
    { label: 'Supervisor aggregation', status: supervisor, description: STEP_DESCRIPTIONS[2] },
    { label: 'Final prediction', status: final, description: STEP_DESCRIPTIONS[3] },
  ]

  // Calculate progress percentage for the connecting bar
  const completedCount = steps.filter((s) => s.status === 'done').length
  const activeIndex = steps.findIndex((s) => s.status === 'active')
  const progressPercent = activeIndex >= 0
    ? (activeIndex * 33.33) + (33.33 / 2) // Active step is halfway through its segment
    : completedCount * 33.33

  return (
    <section
      className="mt-6 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5"
      aria-label="Forecast progress"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {steps.map((s, index) => {
          const isDone = s.status === 'done'
          const isActive = s.status === 'active'
          const isTodo = s.status === 'todo'
          return (
            <div
              key={s.label}
              className={[
                'rounded-delphi',
                'border',
                'px-3',
                'py-3',
                'transition-all',
                isDone
                  ? 'border-green/30 bg-green/10'
                  : isActive
                    ? 'border-green/40 bg-green/10 animate-pulse'
                    : isTodo
                      ? 'border-rule/50 bg-paper/50 opacity-60'
                      : 'border-rule bg-paper',
              ].join(' ')}
            >
              <div className="flex items-center gap-3">
                <span
                  className={[
                    'h-7 w-7 rounded-full border grid place-items-center text-xs shadow-delphi transition-all',
                    isDone
                      ? 'border-green/30 bg-green/10 text-green'
                      : isActive
                        ? 'border-green/40 bg-green/20 text-green animate-pulse'
                        : isTodo
                          ? 'border-rule/50 bg-paper2/50 text-muted opacity-60'
                          : 'border-rule bg-paper2 text-muted',
                  ].join(' ')}
                >
                  {index + 1}
                </span>
                <span
                  className={[
                    'font-semibold transition-colors',
                    isTodo ? 'text-muted opacity-60' : 'text-ink',
                  ].join(' ')}
                >
                  {s.label}
                </span>
              </div>
              <div
                className={[
                  'mt-1 text-sm transition-colors',
                  isTodo ? 'text-muted opacity-50' : 'text-muted',
                ].join(' ')}
              >
                {s.description}
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-4">
        <div className="h-2 rounded-full bg-black/10 overflow-hidden">
          <div
            className="h-full bg-green/50 transition-[width]"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
      </div>
    </section>
  )
}



